/**
 * Secure-storage accessor for the Bluetooth pre-shared key (PSK).
 *
 * The PSK is a 16-byte hex secret shared across all OutdoorLink units.
 * On first access, the build-time value from `bluetoothConfig` is seeded
 * into device secure storage so the key never travels through JS state
 * unprotected after that point. All subsequent reads come from secure storage.
 *
 * Contains:
 * - getSharedKey — primary read/seed accessor (only PSK entry-point for callers)
 * - setSharedKey — explicit write (key rotation, provisioning)
 */

import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

import { getConfiguredPsk } from './bluetoothConfig';

const SHARED_KEY_STORAGE_ID = 'bluetooth_app_shared_key';

/**
 * Returns the Bluetooth PSK hex string from secure storage.
 *
 * If the key is absent (first run), seeds it from the build-time config
 * value returned by `getConfiguredPsk()` and then returns it. This is
 * the only place the build-time config value is copied into secure storage.
 *
 * @returns {Promise<string>} The PSK hex string.
 * @throws {Error} If secure storage fails and the build-time config is also unset.
 */
export async function getSharedKey() {
  try {
    const result = await SecureStoragePlugin.get({ key: SHARED_KEY_STORAGE_ID });

    if (result.value) {
      return result.value;
    }

    // Not yet seeded — copy the build-time value into secure storage.
    const psk = getConfiguredPsk();
    await setSharedKey(psk);
    return psk;
  } catch {
    // SecureStoragePlugin.get throws when the key is absent on some platforms;
    // treat that the same as a missing value and seed from config.
    const psk = getConfiguredPsk();
    try {
      await setSharedKey(psk);
    } catch (setError) {
      console.error('[SharedKey] Failed to seed PSK into secure storage:', setError);
      // Return the config value anyway so auth can proceed this session.
    }
    return psk;
  }
}

/**
 * Writes the PSK hex string to secure storage.
 *
 * Use for key rotation or provisioning flows. Normal callers should use
 * `getSharedKey()` which seeds on first access automatically.
 *
 * @param {string} key - The PSK hex string to store.
 * @returns {Promise<void>}
 */
async function setSharedKey(key) {
  await SecureStoragePlugin.set({
    key: SHARED_KEY_STORAGE_ID,
    value: key,
  });
}
